import { motion } from 'framer-motion';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { toast, Toaster } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // EmailJS configuration - Add your EmailJS credentials to .env
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message,
          to_email: 'amit.bhamere@appdroidtech.com',
        },
        publicKey
      );

      toast.success('Message sent successfully!', {
        description: "We'll get back to you soon.",
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });
    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or email us directly.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen">
      <Toaster position="top-right" richColors />

      {/* Hero */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-none uppercase mb-6">
              Let's Build
              <br />
              <span className="text-purple-500">Something Great</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted max-w-2xl mx-auto">
              Ready to transform your brand? Get in touch.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight mb-8">
                  Get in Touch
                </h2>
                <p className="text-lg text-muted leading-relaxed mb-12">
                  Whether you need cutting-edge software, stunning AI-generated content, or strategic
                  digital marketing, we're here to help. Let's discuss your project.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center text-purple-500 flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Email</h3>
                    <a
                      href="mailto:amit.bhamere@appdroidtech.com"
                      className="text-muted hover:text-purple-500 transition-colors"
                    >
                      amit.bhamere@appdroidtech.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center text-purple-500 flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Phone</h3>
                    <p className="text-muted">+91 8999809160</p>
                    <p className="text-muted">+91 8788343984</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center text-purple-500 flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Location</h3>
                    <p className="text-muted">Jalgaon, Maharashtra, India</p>
                  </div>
                </div>
              </div>

              {/* <div className="pt-8">
                <h3 className="font-bold mb-4">Our Websites</h3>
                <div className="space-y-2">
                  <a
                    href="https://appdroidtech.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-purple-500 hover:text-purple-400 transition-colors"
                  >
                    appdroidtech.com
                  </a>
                  <a
                    href="https://fusionscope.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-purple-500 hover:text-purple-400 transition-colors"
                  >
                    fusionscope.in
                  </a>
                </div>
              </div> */}
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="bg-card border border-themed rounded-2xl p-8 space-y-6"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-bold mb-2 text-primary">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="contact-name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-secondary border border-themed rounded-lg px-4 py-3 text-primary placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold mb-2 text-primary">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-secondary border border-themed rounded-lg px-4 py-3 text-primary placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-bold mb-2 text-primary">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    data-testid="contact-phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-secondary border border-themed rounded-lg px-4 py-3 text-primary placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-bold mb-2 text-primary">
                    Service Interested In *
                  </label>
                  <select
                    id="service"
                    name="service"
                    data-testid="contact-service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full bg-secondary border border-themed rounded-lg px-4 py-3 text-primary focus:outline-none focus:border-purple-500 transition-colors"
                  >
                    <option value="">Select a service</option>
                    <option value="App Development">App Development</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Digital Marketing">Digital Marketing</option>
                    <option value="AI Product Shoots">AI Product Shoots</option>
                    <option value="AI Cinematic Content">AI Cinematic Content</option>
                    <option value="Character Design">Character Design</option>
                    <option value="Brand Films">Brand Films</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold mb-2 text-primary">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-secondary border border-themed rounded-lg px-4 py-3 text-primary placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-full font-bold tracking-wide transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(124,58,237,0.5)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;